import { ImageCroppperResponse } from './../../../../shared/models/image-cropper-resp';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UiStateStore } from 'src/app/shared/store/ui-state-store.service';
import { BusinessDetails } from 'src/app/dashboards/models/business-details';
import { DataService } from 'src/app/dashboards/services/data.service';
import { AppStore } from 'src/app/shared/store/lookup-store.service';
import { EmployerStore } from 'src/app/dashboards/store/employer-store';
import { UploadStatus } from 'src/app/shared/models/upload-status';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs/internal/Observable';
import { of, Subscription } from 'rxjs';
import { Router } from '@angular/router';

@Component({
  selector: 'app-dashboard-employer-profile',
  templateUrl: './dashboard-profile.component.html',
  styleUrls: ['./dashboard-profile.component.scss'],
})
export class DashboardEmployerProfileComponent implements OnInit, OnDestroy {
  form: FormGroup;
  accountId: string;
  uploadStatus: string;
  photoPath: string = 'assets/images/bg/cover-bg.png';
  subscriptions: Subscription[] = [];

  constructor(
    private formBuilder: FormBuilder,
    public uiState: UiStateStore,
    public lookupStore: AppStore,
    public dataService: DataService,
    public store: EmployerStore,
    public router: Router
  ) { }
  @HostListener('window:beforeunload')
  checkCanDeactivate(): Observable<boolean> | boolean {
    return !this.form.dirty;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  ngOnInit() {
    this.subscriptions.push(
      this.store.profilePhoto.subscribe((path) => {
        if (!path) {
          return;
        }

        if (this.form) {
          this.form.patchValue({ photoPath: path });
        }

        this.photoPath = `${environment.baseUrl}/${path}`;
      }));

    this.subscriptions.push(
      this.uiState.authenticatedUser.subscribe((user) => {
        if (!user) {
          return;
        }
        this.accountId = user.accountId;
        this.dataService
          .getBusinessDetails(this.accountId)
          .subscribe((details) => { });
      }));

    this.subscriptions.push(
      this.store.BusinessDetails.subscribe((details) => {
        if (!details) {
          return;
        }

        if (!this.form) {
          return;
        }

        this.form.patchValue({ id: details.id });
        this.form.patchValue({ accountId: details.id });
        this.form.patchValue({ companyName: details.companyName });
        this.form.patchValue({ email: details.email });
        this.form.patchValue({ phone: details.phone });
        this.form.patchValue({ firstName: details.firstName });
        this.form.patchValue({ lastName: details.lastName });
        this.form.patchValue({ sector: details.sector });
        this.form.patchValue({ address: details.address });
        this.form.patchValue({ description: details.description });
        this.form.patchValue({ website: details.website });
        this.form.patchValue({ facebook: details.facebook });
        this.form.patchValue({ twitter: details.twitter });
        this.form.patchValue({ linkedin: details.linkedin });
        this.form.patchValue({ photoPath: details.photoPath });
        this.form.patchValue({ city: details.city });
        this.form.patchValue({ country: details.country });
      }));

    this.form = this.formBuilder.group({
      id: [''],
      accountId: [`${this.accountId}`],
      companyName: ['', Validators.required],
      email: [
        '',
        [
          Validators.required,
          Validators.pattern(
            /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          ),
        ],
      ],
      phone: ['', [Validators.required, Validators.pattern('^\\+{0,1}[0-9]{11,14}$')]],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      sector: [''],
      description: [''],
      website: [
        '',
        [
          Validators.pattern('(https?://)?([\\da-z.-]+)\\.([a-z.]{2,6})[/\\w .-]*/?'),
        ],
      ],
      facebook: [
        '',
        [
          Validators.pattern(
            /(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)?/g
          ),
        ],
      ],
      twitter: [
        '',
        [
          Validators.pattern(
            /(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)?/g
          ),
        ],
      ],
      linkedin: [
        '',
        [
          Validators.pattern(
            /(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)?/g
          ),
        ],
      ],
      photoPath: [''],
      address: ['', Validators.required],
      city: ['', Validators.required],
      country: ['', Validators.required],
    });
  }

  saveProfile(): void {
    if (!this.form.valid) {
      return;
    }

    let businessDetails = BusinessDetails.parseForm(this.form);
    this.dataService.saveBusinessDetails(businessDetails).subscribe(r => {
      this.form.reset(this.form.value);
      window.scroll({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
      setTimeout(() => {
        this.router.navigate(['/employer/manage-jobs']);
      }, 1000);
    });
  }

  uploadFinished(status: UploadStatus): void {
    this.uploadStatus = status.status;
    setTimeout(() => {
      this.uploadStatus = '';
    }, 500);

    this.store.setProfilePath(status.filePath);
    this.form.markAsDirty();
  }

  handleFileChangeEvent = async (eventResp: ImageCroppperResponse) => {
    if (!eventResp.filePath) {
      this.uploadStatus = 'Unable to upload the file.';
      return false;
    }
    this.store.setProfilePath(eventResp.filePath);
    this.form.markAsDirty();
  };

  canDeactivate = (): Observable<boolean> | boolean => {
    if (this.form.dirty) {
      const result = window.confirm(
        'There are unsaved changes in profile ! Are you sure want to discrad?'
      );
      return of(result);
    }
    return of(true);
  };
}
